import React, {useEffect, useRef, useState} from "react";

import {getLocation} from "../../../_services/location.service";
import useDebounce from "../../../contexts/useDebounce";
import {IoBusinessOutline} from "react-icons/io5";
import './LocationDropdown.css';

const HotelDestinationIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.33331 18.3334V12.8584" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 9.16675H10.0083" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 5.83325H10.0083" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11.6667 12.8584V18.3334" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12.5 13.3333C11.7788 12.7924 10.9015 12.5 10 12.5C9.09846 12.5 8.22123 12.7924 7.5 13.3333" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.3333 9.16675H13.3416" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.3333 5.83325H13.3416" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.66669 9.16675H6.67502" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.66669 5.83325H6.67502" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15 1.66675H4.99998C4.07951 1.66675 3.33331 2.41294 3.33331 3.33341V16.6667C3.33331 17.5872 4.07951 18.3334 4.99998 18.3334H15C15.9205 18.3334 16.6666 17.5872 16.6666 16.6667V3.33341C16.6666 2.41294 15.9205 1.66675 15 1.66675Z" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const DefaultDestinationIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 15 18" fill="none">
        <path d="M12.2917 0.625H2.29167C1.37119 0.625 0.625 1.37119 0.625 2.29167V15.625C0.625 16.5455 1.37119 17.2917 2.29167 17.2917H12.2917C13.2121 17.2917 13.9583 16.5455 13.9583 15.625V2.29167C13.9583 1.37119 13.2121 0.625 12.2917 0.625Z" stroke="black" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 5L5 15" stroke="#02003C" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M5 5L15 15" stroke="#02003C" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

export default function SearchLocation({placeholder, disabled = false, location, onChange, variant}) {

    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [displayValue, setDisplayValue] = useState(location?.name || '')
    const [open, setOpen] = useState(false)
    const [list, setList] = useState([])
    const [isFocused, setIsFocused] = useState(false)

    const hasValidSelectionRef = useRef(!!location?.id)

    const inputRef = useRef(null);
    const labelText = placeholder || 'Destino';
    const ariaLabel = labelText;
    const isHotel = variant === 'hotel';

    useEffect(() => {
        setDisplayValue(location?.name || '')
        hasValidSelectionRef.current = !!location?.id
    }, [location])

    const debouncedChange = useDebounce(setSearch, 500, () => {});

    const safeDisplayValue = typeof displayValue === 'string' ? displayValue : '';
    const safeSearch = typeof search === 'string' ? search : '';
    const safeList = Array.isArray(list) ? list : [];

    const handleChange = (e) => {
        const value = typeof e?.target?.value === 'string' ? e.target.value : '';
        const hadValidSelection = hasValidSelectionRef.current;

        if (value.length >= 3) {
            setLoading(true)
            if (isFocused) setOpen(true)
        } else {
            setLoading(false)
            setOpen(false)
            setList([])
        }
        setDisplayValue(value);
        debouncedChange(value);
        clearLocationValidationError();

        hasValidSelectionRef.current = false;

        // Quando o usuário volta a digitar no destino, invalida a seleção anterior imediatamente.
        // Isso mantém o fluxo de habilitação do campo "Nome do hotel" apenas após seleção válida.
        if (hadValidSelection) {
            onChange({});
        }
    }

    useEffect(() => {
        const searchLocation = async () => {
            setLoading(true)
            const res = await getLocation(search, 9999);
            setList(res?.data?.locations ?? [])
        }

        if (safeSearch.length >= 3) {
            searchLocation().then(() => {
                setLoading(false)
            })
        } else {
            setList([])
            setLoading(false)
        }

    }, [search])

    const clearSelection = () => {
        debouncedChange.cancel();
        setDisplayValue('')
        setSearch('')
        setList([])
        setOpen(false)
        setLoading(false)
        hasValidSelectionRef.current = false
        onChange({})
        clearLocationValidationError()

        if (inputRef.current) {
            inputRef.current.focus()
            setIsFocused(true)
        }
    }

    const addLocationValidationError = (message) => {
        if (!isHotel) return;
        const element = document.getElementById('hotLocation');
        if (element) {
            const inputGroup = element.closest('.input-group');
            if (inputGroup) {
                inputGroup.classList.add('validation-error');
                const existingMsg = inputGroup.parentNode.querySelector('.validation-error-message');
                if (existingMsg) {
                    existingMsg.textContent = message;
                    return;
                }
                const errorMsg = document.createElement('div');
                errorMsg.className = 'validation-error-message';
                errorMsg.textContent = message;
                inputGroup.parentNode.insertBefore(errorMsg, inputGroup.nextSibling);
            }
        }
    }

    const clearLocationValidationError = () => {
        const element = document.getElementById('hotLocation');
        if (element) {
            const inputGroup = element.closest('.input-group');
            if (inputGroup) {
                inputGroup.classList.remove('validation-error');
                const errorMsg = inputGroup.parentNode.querySelector('.validation-error-message');
                if (errorMsg) {
                    errorMsg.remove();
                }
            }
        }
    }

    const changeLocation = (e, params) => {
        e.preventDefault();
        onChange(params)
        setDisplayValue(params.name)
        setOpen(false)
        setList([])

        hasValidSelectionRef.current = true;

        clearLocationValidationError();

        if (inputRef.current) {
            inputRef.current.focus()
            setIsFocused(true)
        }
    }

    const handleBlur = () => {
        setIsFocused(false);
        setTimeout(() => {
            setOpen(false);

            if (safeDisplayValue && !hasValidSelectionRef.current && safeDisplayValue.trim().length >= 3) {
                addLocationValidationError('Selecione uma cidade válida da lista');
                onChange({});
            } else {
                clearLocationValidationError();
            }
        }, 200);
    }

    const hasValue = !!safeDisplayValue.trim().length;
    const shouldShowDropdown = open && isFocused;

    return (
        <div className="dropdown">
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    minHeight: '62px',
                    padding: '16px',
                    border: '1px solid #D8D8D8',
                    borderRadius: '4px',
                    background: disabled ? '#f5f5f5' : 'transparent',
                    position: 'relative'
                }}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', minWidth: '20px', minHeight: '20px' }}>
                    {isHotel ? <HotelDestinationIcon /> : <DefaultDestinationIcon />}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: hasValue ? '4px' : '0px', flex: 1, minWidth: 0 }}>
                    {hasValue && (
                        <label
                            style={{
                                color: '#4F4F4F',
                                letterSpacing: '-0.8px',
                                margin: 0,
                                lineHeight: '1',
                                fontSize: '12px',
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 400
                            }}
                        >
                            {labelText}
                        </label>
                    )}
                    <input
                        ref={inputRef}
                        className="form-control form-control-custom border-custom-none destination-input"
                        value={safeDisplayValue}
                        onChange={handleChange}
                        onFocus={() => {
                            setIsFocused(true);
                            if (safeDisplayValue.length >= 3) setOpen(true);
                        }}
                        onBlur={handleBlur}
                        onClick={() => safeDisplayValue.length >= 3 && setOpen(true)}
                        placeholder={!hasValue ? labelText : ""}
                        disabled={disabled}
                        id='hotLocation'
                        autoComplete="off"
                        aria-label={ariaLabel}
                        style={{
                            border: 'none',
                            boxShadow: 'none',
                            padding: 0,
                            height: !hasValue ? 'auto' : '20px',
                            fontSize: '14px',
                            fontWeight: !hasValue ? 400 : 600,
                            fontFamily: 'Montserrat, sans-serif',
                            color: '#4F4F4F',
                            background: 'transparent',
                            textOverflow: 'ellipsis'
                        }}
                    />
                </div>
                {safeDisplayValue && isFocused && (
                    <button
                        type="button"
                        aria-label="Limpar"
                        onMouseDown={(e) => { e.preventDefault(); clearSelection(); }}
                        className="btn btn-link p-0 m-0"
                        style={{ color: '#0B0D40', minWidth: '20px', position: 'absolute', right: 15, top: '50%', transform: 'translateY(-50%)' }}
                    >
                        <CloseIcon />
                    </button>
                )}
            </div>

            <ul
                className={`dropdown-menu custom-scroll-dropdown w-100 ${shouldShowDropdown && 'show'}`}
                onMouseDown={(e) => e.preventDefault()}
            >
                {
                    loading ? <li className="text-center">Buscando...</li> :

                        !safeList.length
                            ? <li className="text-center">Nenhum local encontrado</li>

                            : (
                                <>
                                    {isHotel && (
                                        <li
                                            style={{
                                                background: '#f7f7f7',
                                                padding: '8px 12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                fontSize: '12px',
                                                color: '#000',
                                                letterSpacing: '-0.8px'
                                            }}
                                        >
                                            <IoBusinessOutline size={16} />
                                            Cidades
                                        </li>
                                    )}
                                    {safeList.sort((a, b) => a.name.localeCompare(b.name))
                                        .map(item =>
                                        <li key={item.id}>
                                            <a className="dropdown-item text-wrap" href="#"
                                               onClick={(e) => changeLocation(e, {id: item.id, name: item.name})}>{item.name}</a>
                                        </li>
                                    )}
                                </>
                            )
                }
            </ul>
        </div>
    )
}
